import React, { useState } from 'react';
import { 
  X, Video, Image as ImageIcon, Link as LinkIcon, 
  ShieldAlert, Dumbbell, Clock, Repeat, RotateCcw, 
  Target, Globe, User, Sparkles, CheckCircle2, Play
} from 'lucide-react';
import { Exercise, ProgramDayExercise } from '../types';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { getLocalizedDifficulty } from '../lib/exerciseTranslations';
import { parseVideoMetadata, getEmbedVideoUrl } from '../lib/videoUtils';

interface ExerciseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise | ProgramDayExercise | null;
  onStartWorkout?: (exercise: Exercise | ProgramDayExercise) => void;
  onAddToPlanner?: (exercise: Exercise | ProgramDayExercise) => void;
}

export default function ExerciseDetailModal({
  isOpen,
  onClose,
  exercise,
  onStartWorkout,
  onAddToPlanner
}: ExerciseDetailModalProps) {
  const { t, lang } = useLanguage();

  if (!isOpen || !exercise) return null;

  const normalizedExercise: Exercise = {
    id: (exercise as any).id || (exercise as any).exerciseId || 'temp_ex',
    name: exercise.name,
    category: exercise.category || 'rehab',
    description: exercise.description || '',
    purpose: exercise.purpose || '',
    targetMuscles: exercise.targetMuscles || '',
    sets: exercise.sets || 3,
    reps: exercise.reps || '10',
    rest: exercise.rest || '20',
    duration: exercise.duration || 5,
    difficulty: exercise.difficulty || 'Beginner',
    precautions: (exercise as any).precautions,
    equipment: (exercise as any).equipment,
    image: exercise.image,
    videoUrl: exercise.videoUrl,
    scope: (exercise as any).scope || ((exercise as any).user_id ? 'personal' : 'global')
  };

  const locEx = normalizedExercise;

  const [activeMediaTab, setActiveMediaTab] = useState<'video' | 'image'>(
    exercise.videoUrl ? 'video' : 'image'
  );

  const videoUrl = exercise.videoUrl;
  const imageUrl = exercise.image;

  const videoMeta = videoUrl ? parseVideoMetadata(videoUrl) : null;
  const embedUrl = videoMeta?.embedUrl || (videoUrl ? getEmbedVideoUrl(videoUrl) : null);
  const isDirectVideo = Boolean(
    videoMeta?.isDirectVideo || 
    (videoUrl && (
      videoUrl.startsWith('data:video') || 
      videoUrl.startsWith('blob:') ||
      videoUrl.endsWith('.mp4') || 
      videoUrl.endsWith('.webm') || 
      videoUrl.endsWith('.mov') ||
      videoUrl.includes('/api/files/')
    ))
  );
  const isInstagram = videoMeta?.provider === 'instagram' || Boolean(videoUrl && (videoUrl.includes('instagram.com') || videoUrl.includes('instagr.am')));

  const isGlobal = (exercise as any).scope === 'global' || ((exercise as any).scope !== 'personal' && !(exercise as any).user_id);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-xs animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-purple-200 dark:border-purple-900/50 w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Modal Header */}
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 dark:from-purple-950/40 dark:via-indigo-950/40 dark:to-purple-950/40 flex items-start justify-between gap-3">
          <div className="space-y-1.5 flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {/* Scope Badge */}
              {isGlobal ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                  <Globe className="w-3 h-3" />
                  <span>{t('rehabScopeGlobalBadge', 'عمومی')}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                  <User className="w-3 h-3" />
                  <span>{t('rehabScopePersonalBadge', 'شخصی')}</span>
                </span>
              )}

              {/* Category Badge */}
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300">
                {exercise.category === 'fat-loss' 
                  ? t('rehabCardTagFatLoss', 'چربی‌سوزی و تمرین هوازی') 
                  : t('rehabCardTagRehab', 'فیزیوتراپی و حرکات اصلاحی')}
              </span>

              {/* Difficulty Badge */}
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {getLocalizedDifficulty(exercise.difficulty, lang)}
              </span>
            </div>

            <h3 className="text-base sm:text-lg font-extrabold text-gray-900 dark:text-white leading-snug truncate">
              {locEx.name}
            </h3>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer shrink-0"
            title={t('close', 'بستن')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
          
          {/* Media Section: Video and/or Image */}
          {(videoUrl || imageUrl) && (
            <div className="space-y-2">
              {/* Media Tab Switcher if both are present */}
              {videoUrl && imageUrl && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setActiveMediaTab('video')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                      activeMediaTab === 'video'
                        ? 'bg-purple-600 text-white shadow-xs'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    <Video className="w-3.5 h-3.5" />
                    <span>{t('rehabPlayVideo', 'مشاهده فیلم آموزشی')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveMediaTab('image')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                      activeMediaTab === 'image'
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    <ImageIcon className="w-3.5 h-3.5" />
                    <span>{t('rehabPlayImage', 'مشاهده تصویر حرکت')}</span>
                  </button>
                </div>
              )}

              {/* Video Player */}
              {(activeMediaTab === 'video' || !imageUrl) && videoUrl && (
                <div className="overflow-hidden rounded-2xl border border-purple-200 dark:border-purple-900 bg-black shadow-md flex flex-col items-center">
                  {embedUrl ? (
                    <div className={`relative w-full ${isInstagram ? 'h-[480px] max-w-sm mx-auto' : 'aspect-video'}`}>
                      <iframe
                        src={embedUrl}
                        title={locEx.name}
                        className="w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : isDirectVideo ? (
                    <video
                      src={videoUrl}
                      controls
                      controlsList="nodownload"
                      preload="metadata"
                      className="w-full max-h-80 object-contain mx-auto"
                    />
                  ) : (
                    <div className="p-4 text-center bg-gray-900 w-full space-y-2">
                      <a
                        href={videoUrl.startsWith('http') ? videoUrl : `https://${videoUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition-all shadow-md"
                      >
                        <Video className="w-4 h-4" />
                        <span>{t('rehabVideoWeb', 'مشاهده و پخش لینک ویدیو (اینستاگرام / وب)')}</span>
                      </a>
                    </div>
                  )}

                  {isInstagram && (
                    <div className="p-2.5 bg-gray-950 w-full text-center border-t border-gray-800 flex items-center justify-between px-4">
                      <span className="text-[11px] text-gray-400 font-semibold">{t('rehabVideoIg', 'پست / ریلز اینستاگرام')}</span>
                      <a
                        href={videoUrl.startsWith('http') ? videoUrl : `https://${videoUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-purple-400 hover:text-purple-300 font-bold inline-flex items-center gap-1 hover:underline"
                      >
                        <span>{t('rehabVideoIgOpen', 'باز کردن مستقیم در اپ اینستاگرام')}</span>
                        <LinkIcon className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Image Preview */}
              {(activeMediaTab === 'image' || !videoUrl) && imageUrl && (
                <div className="flex justify-center items-center p-3 bg-gray-50/80 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <img
                    src={imageUrl}
                    alt={locEx.name}
                    className="max-h-80 w-auto object-contain rounded-xl shadow-sm"
                    referrerPolicy="no-referrer"
                  />
                </div>
              )}
            </div>
          )}

          {/* Full Instructions / Description */}
          <div className="p-4 bg-purple-50/50 dark:bg-purple-950/20 rounded-2xl border border-purple-100 dark:border-purple-900/30 space-y-1.5">
            <span className="text-[11px] font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider block">
              {t('rehabFullInstructions', 'دستورالعمل و نحوه اجرای کامل:')}
            </span>
            <p className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-line">
              {locEx.description || t('noProgramDesc', 'توضیحاتی برای این حرکت ثبت نشده است.')}
            </p>
          </div>

          {/* Purpose & Target Muscles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-2xl border border-gray-200 dark:border-gray-700/80 space-y-1">
              <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                <span>{t('rehabTargetPurpose', 'هدف تمرین و فایده درمانی:')}</span>
              </span>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                {locEx.purpose || '—'}
              </p>
            </div>

            <div className="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-2xl border border-gray-200 dark:border-gray-700/80 space-y-1">
              <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Dumbbell className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <span>{t('rehabTargetMuscles', 'عضلات و مفاصل درگیر:')}</span>
              </span>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                {locEx.targetMuscles || '—'}
              </p>
            </div>
          </div>

          {/* Prescription Grid: Sets, Reps, Rest, Duration */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-3.5 bg-gray-50 dark:bg-gray-800/40 rounded-2xl border border-gray-200 dark:border-gray-700 text-center">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-xl shadow-xs">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 block mb-1">
                {t('rehabCustomSets', 'تعداد ست')}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                {formatNumberByLang(locEx.sets || 3, lang)}
              </span>
            </div>

            <div className="p-2 bg-white dark:bg-gray-800 rounded-xl shadow-xs">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 block mb-1">
                {t('rehabCustomReps', 'تکرار / مکث')}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {locEx.reps || '10'}
              </span>
            </div>

            <div className="p-2 bg-white dark:bg-gray-800 rounded-xl shadow-xs">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 block mb-1">
                {t('rehabCustomRest', 'استراحت بین ست')}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {locEx.rest || '20s'}
              </span>
            </div>

            <div className="p-2 bg-white dark:bg-gray-800 rounded-xl shadow-xs">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 block mb-1">
                {t('rehabCustomDuration', 'مدت زمان (دقیقه)')}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                {formatNumberByLang(locEx.duration || 5, lang)} {t('minuteUnit', 'دقیقه')}
              </span>
            </div>
          </div>

          {/* Precautions & Safety Notes (If available) */}
          {locEx.precautions && (
            <div className="p-3.5 bg-amber-50 dark:bg-amber-950/30 rounded-2xl border border-amber-200 dark:border-amber-800/50 space-y-1">
              <span className="text-[11px] font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span>{t('exercisePrecautionsLabel', 'نکات ایمنی و موارد منع مصرف:')}</span>
              </span>
              <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                {locEx.precautions}
              </p>
            </div>
          )}

          {/* Equipment Needed (If available) */}
          {locEx.equipment && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-2xl border border-blue-200 dark:border-blue-800/40 text-xs flex items-center gap-2 text-blue-900 dark:text-blue-200">
              <Dumbbell className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <div>
                <strong className="font-bold ml-1">{t('exerciseEquipmentLabel', 'تجهیزات مورد نیاز:')}</strong>
                <span>{locEx.equipment}</span>
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {onAddToPlanner && (
              <button
                type="button"
                onClick={() => onAddToPlanner(exercise)}
                className="px-3.5 py-2 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-semibold rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/60 flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>{t('scheduleDayBtn', 'افزودن به روتین هفتگی')}</span>
              </button>
            )}

            {onStartWorkout && (
              <button
                type="button"
                onClick={() => onStartWorkout(exercise)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                <span>{t('runWorkoutForDay', 'اجرای تمرین')}</span>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
          >
            {t('close', 'بستن')}
          </button>
        </div>

      </div>
    </div>
  );
}
